import { findNewsSource } from '@tsr/shared-types';
import { YouTubeLivePlayer } from './YouTubeLivePlayer';
import { SignalMedia } from './SignalMedia';

/**
 * News media tile (#182 — YouTube news reel; #65/#183 wave 3b — wire signals).
 *
 * A kind:'news' pin carries one of TWO id shapes, branched here so every
 * pin-rendering surface (panel media region, wall mosaic) stays unchanged:
 *   • a NEWS_SOURCES registry slug (#182) → the curated outlet's CURRENT live
 *     broadcast via the shared YouTube-live player (`/api/news/:id/youtube`,
 *     the camera path's channel-live resolver) — same liveness / "not live" /
 *     "unavailable" chrome as the camera tiles, no duplicate player;
 *   • anything else = a media_signals uuid (a wave-3b pinned wire signal) →
 *     the honest static post card (`SignalMedia`) — an article/post is not a
 *     live stream and is never dressed as one.
 *
 * News is AMBIENT: pop-out is off by default (the panel/wall IS the surface)
 * and there is no red/anomaly treatment anywhere here.
 */
export function NewsMedia({
  id,
  label,
  enablePopout = false,
  fill = false,
  chromeless = false,
}: {
  id: string;
  label: string;
  enablePopout?: boolean;
  /** Wall fill mode (#232) — passed through to the shared YouTube player.
   *  (A SignalMedia post card is a static card either way — no fill form.) */
  fill?: boolean;
  /** Suppress embed + overlay chrome (#232) — passed through likewise. */
  chromeless?: boolean;
}): JSX.Element {
  if (!findNewsSource(id)) {
    return <SignalMedia id={id} label={label} />;
  }
  return (
    <YouTubeLivePlayer
      cameraId={id}
      name={label}
      enablePopout={enablePopout}
      resolveUrl={`/api/news/${encodeURIComponent(id)}/youtube`}
      fill={fill}
      chromeless={chromeless}
    />
  );
}
